import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  RefreshCw, Plus, Pencil, Trash2, CalendarDays, MapPin, Bell, Lock,
} from "lucide-react";
import BudgetPanel from "@/components/budget/BudgetPanel";
import PanelLayout from "@/components/layout/PanelLayout";
import DemoWriteBadge from "@/components/demo/DemoWriteBadge";
import {
  CalendarToolbar, CategoryMultiSelect, DayView, WeekView, MonthView,
  COLOR_KEYS, COLOR_CLASS, ALL_CATEGORIES, colorFor,
} from "@/components/calendar/CalendarViews";

/**
 * A20-P7 — Inline mini-list of wishlist items linked to a calendar event.
 * Lives next to the BudgetPanel inside the event-edit drawer.
 * Read-only here; deep-link to /app/wishlists for management.
 */
const LinkedWishlistItems = ({ eventId, t }) => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/wishlists/by-event/${eventId}`)
      .then((r) => { if (alive) { setItems(r.data?.items || []); setLoaded(true); } })
      .catch(() => { if (alive) { setItems([]); setLoaded(true); } });
    return () => { alive = false; };
  }, [eventId]);
  if (!loaded) return null;
  return (
    <div className="pt-3 mt-3 border-t border-slate-200" data-testid="my-calendar-linked-items-section">
      <h4 className="text-xs font-semibold text-slate-700 mb-2">
        {t("wishlist.link.inCalendarSection")} ({items.length})
      </h4>
      {items.length === 0 ? (
        <p className="text-xs text-slate-500" data-testid="my-calendar-linked-items-empty">
          {t("wishlist.link.inCalendarEmpty")}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((it) => (
            <li
              key={it.id}
              className="bg-violet-50 border border-violet-200 rounded px-2.5 py-1.5 text-xs flex items-center justify-between gap-2"
              data-testid={`my-calendar-linked-item-${it.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 truncate">{it.title}</div>
                <div className="text-slate-500 text-[10px]">
                  {it.category} · {it.priority} · {it.status}
                </div>
              </div>
              {it.required_by_date && (
                <span className="text-[10px] text-slate-600 tabular-nums shrink-0">
                  {it.required_by_date}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * A20-P1 — Personal calendar (Preview-only, per-user only).
 *
 * Path: /app/calendar
 *
 * Per A20-P0 decision P1: per-user calendar only in P1. No shared
 * team/family layer (lands in A20-P2). No external sync (Google
 * Calendar / WhatsApp arrive in A20-P12/P13 planning).
 */

const CATEGORIES = ["training", "match", "travel", "meeting", "deadline", "personal", "other"];
const COLORS = ["slate", "emerald", "violet", "sky", "amber", "rose"];
const REMINDER_MINUTES = [5, 15, 30, 60, 120, 360, 1440, 2880, 10080];

const COLOR_RAIL = {
  slate: "bg-slate-500", emerald: "bg-emerald-500", violet: "bg-violet-500",
  sky: "bg-sky-500", amber: "bg-amber-500", rose: "bg-rose-500",
};
const COLOR_BADGE = {
  slate: "bg-slate-100 text-slate-800",
  emerald: "bg-emerald-100 text-emerald-900",
  violet:  "bg-violet-100 text-violet-900",
  sky:     "bg-sky-100 text-sky-900",
  amber:   "bg-amber-100 text-amber-900",
  rose:    "bg-rose-100 text-rose-900",
};

const isoToLocal = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};
const localToIso = (loc) => {
  if (!loc) return "";
  try { return new Date(loc).toISOString(); } catch { return loc; }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch { return iso; }
};

const emptyForm = () => {
  const now = new Date();
  const later = new Date(now.getTime() + 60 * 60 * 1000);
  return {
    title: "",
    description: "",
    location: "",
    starts_at: isoToLocal(now.toISOString()),
    ends_at: isoToLocal(later.toISOString()),
    all_day: false,
    color: "emerald",
    category: "personal",
    // A20-P1.1 — additive fields. `categories[]` (multi-select), `color_key`
    // (richer 13-color enum), `other_category_text` (free text mandatory
    // when `other` ∈ categories).
    categories: [],
    other_category_text: "",
    color_key: "",
    reminders: [60],
  };
};

const MyCalendar = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ category: "", from_date: "", to_date: "" });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  // A20-P1.1 — visual calendar state. Lives alongside the legacy list so
  // we can ship Day/Week/Month without removing the existing list UX.
  const [viewMode, setViewMode] = useState("week");        // day | week | month | list
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [selectedCats, setSelectedCats] = useState(new Set());

  const fetchAll = useCallback(async () => {
    // A20-P1.1-fix — never query backend with invalid range. The UI
    // surfaces the inline error; the request is simply skipped so we
    // don't get a 400 toast on top.
    if (filters.from_date && filters.to_date && filters.to_date < filters.from_date) {
      return;
    }
    setLoading(true);
    try {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.from_date) params.from_date = new Date(filters.from_date).toISOString();
      if (filters.to_date) params.to_date = new Date(filters.to_date).toISOString();
      const res = await api.get("/calendar/events", { params });
      setRows(res.data?.events || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, "load_failed"));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDrawerOpen(true);
  };
  const openEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title || "",
      description: ev.description || "",
      location: ev.location || "",
      starts_at: isoToLocal(ev.starts_at),
      ends_at: isoToLocal(ev.ends_at),
      all_day: !!ev.all_day,
      color: ev.color || "emerald",
      category: ev.category || "personal",
      categories: Array.isArray(ev.categories) ? ev.categories : [],
      other_category_text: ev.other_category_text || "",
      color_key: ev.color_key || "",
      reminders: Array.isArray(ev.reminders) ? ev.reminders : [],
    });
    setDrawerOpen(true);
  };

  const toggleReminder = (m) => {
    setForm((f) => {
      const has = f.reminders.includes(m);
      const next = has ? f.reminders.filter((x) => x !== m) : [...f.reminders, m];
      return { ...f, reminders: next };
    });
  };

  const submitForm = async () => {
    // POST-DEPLOY-FIX-03 — when creating a new event the start must be
    // now or in the future. Editing existing past events stays allowed
    // so historical records aren't blocked.
    if (!editingId && form.starts_at) {
      const startMs = new Date(form.starts_at).getTime();
      const nowMs = Date.now();
      if (Number.isFinite(startMs) && startMs < nowMs - 60_000) {
        toast.error(t("calendar.toasts.startInPast"));
        return;
      }
    }
    // A20-P1 — client-side date guard mirrors the model_validator on the
    // backend so the user gets an instant, localised error instead of
    // waiting for the round-trip.
    if (form.starts_at && form.ends_at && form.ends_at <= form.starts_at) {
      toast.error(t("calendar.toasts.endBeforeStart"));
      return;
    }
    // A20-P1.1 — "Other" category must be paired with a non-empty text.
    const hasOther = (form.categories || []).includes("other");
    if (hasOther && !(form.other_category_text || "").trim()) {
      toast.error(t("calendar.form.otherRequired"));
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      location: form.location.trim() || null,
      starts_at: localToIso(form.starts_at),
      ends_at: localToIso(form.ends_at),
      all_day: !!form.all_day,
      color: form.color,
      category: form.category,
      // A20-P1.1 — only send the new fields if they have content, so
      // older events stay clean.
      categories: form.categories || [],
      other_category_text: hasOther ? (form.other_category_text || "").trim() : null,
      color_key: form.color_key || null,
      reminders: form.reminders.slice().sort((a, b) => a - b),
    };
    try {
      if (editingId) {
        await api.put(`/calendar/events/${editingId}`, payload);
        toast.success(t("calendar.toasts.updated"));
      } else {
        await api.post("/calendar/events", payload);
        toast.success(t("calendar.toasts.created"));
      }
      setDrawerOpen(false);
      await fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("calendar.toasts.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(t("calendar.confirmDelete"))) return;
    try {
      await api.delete(`/calendar/events/${ev.id}`);
      toast.success(t("calendar.toasts.deleted"));
      await fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("calendar.toasts.saveFailed")));
    }
  };

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (a.starts_at || "").localeCompare(b.starts_at || "")),
    [rows],
  );

  return (
    <PanelLayout>
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="my-calendar-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="my-calendar-title">
              <CalendarDays className="w-6 h-6 text-emerald-700" />
              {t("calendar.title")}
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">{t("calendar.subtitle")}</p>
            <p className="text-[11px] text-slate-400 mt-1 inline-flex items-center gap-1">
              <Lock className="w-3 h-3" />
              {t("calendar.privacyNotice")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={fetchAll} disabled={loading}
              data-testid="my-calendar-refresh"
            >
              <RefreshCw className="w-4 h-4 me-1" />
              {t("calendar.refresh")}
            </Button>
            <Button
              size="sm"
              onClick={openCreate}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="my-calendar-create-btn"
            >
              <Plus className="w-4 h-4 me-1" />
              {t("calendar.new")}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4 grid sm:grid-cols-3 gap-3">
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">{t("calendar.filters.category")}</span>
              <select
                className="w-full border rounded px-2 py-1.5 text-sm"
                value={filters.category}
                onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
                data-testid="my-calendar-filter-category"
              >
                <option value="">{t("calendar.filters.all")}</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{t(`calendar.categories.${c}`)}</option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">{t("calendar.filters.from")}</span>
              <Input
                type="datetime-local"
                value={filters.from_date}
                onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
                data-testid="my-calendar-filter-from"
              />
            </label>
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">{t("calendar.filters.to")}</span>
              <Input
                type="datetime-local"
                value={filters.to_date}
                min={filters.from_date || undefined}
                onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
                data-testid="my-calendar-filter-to"
                aria-invalid={filters.from_date && filters.to_date && filters.to_date < filters.from_date}
              />
              {filters.from_date && filters.to_date && filters.to_date < filters.from_date && (
                <span
                  className="block text-xs text-rose-700 mt-1"
                  data-testid="my-calendar-filter-to-error"
                >
                  {t("calendar.toasts.endBeforeStart")}
                </span>
              )}
            </label>
          </CardContent>
        </Card>

        {/* A20-P1.1 — Visual Calendar (Day / Week / Month). Lives above
            the legacy list. The list stays available below for users who
            still prefer that mental model. */}
        <Card data-testid="my-calendar-visual-card">
          <CardHeader className="pb-3 space-y-3">
            <CalendarToolbar
              viewMode={viewMode}
              setViewMode={setViewMode}
              anchorDate={anchorDate}
              setAnchorDate={setAnchorDate}
            />
            <div className="flex items-center justify-end">
              <CategoryMultiSelect
                selected={selectedCats}
                setSelected={setSelectedCats}
              />
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              // Apply multi-category filter to the in-memory list before
              // routing to the active view.
              const filtered = sortedRows.filter((ev) => {
                if (selectedCats.size === 0) return true;
                const cats = new Set(ev.categories || []);
                if (ev.category) cats.add(ev.category);
                for (const c of cats) if (selectedCats.has(c)) return true;
                return false;
              });
              if (viewMode === "day") {
                return <DayView anchorDate={anchorDate} events={filtered} onEventClick={openEdit} />;
              }
              if (viewMode === "week") {
                return <WeekView anchorDate={anchorDate} events={filtered} onEventClick={openEdit} onDaySelect={(d) => { setAnchorDate(d); setViewMode("day"); }} />;
              }
              return <MonthView anchorDate={anchorDate} events={filtered} onEventClick={openEdit} onDaySelect={(d) => { setAnchorDate(d); setViewMode("day"); }} />;
            })()}
          </CardContent>
        </Card>

        {/* Events list */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{sortedRows.length} {t("calendar.title")}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {sortedRows.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center" data-testid="my-calendar-empty">
                {t("calendar.empty")}
              </p>
            ) : (
              <ul className="space-y-2">
                {sortedRows.map((ev) => (
                  <li
                    key={ev.id}
                    className="relative overflow-hidden rounded-lg border border-slate-200 bg-white p-4 flex items-start gap-3"
                    data-testid={`my-calendar-row-${ev.id}`}
                  >
                    <div className={`absolute inset-y-0 left-0 w-1 ${COLOR_RAIL[ev.color] || COLOR_RAIL.emerald}`} aria-hidden="true" />
                    <div className="flex-1 min-w-0 ps-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-slate-900 truncate">{ev.title}</h3>
                        <Badge className={`${COLOR_BADGE[ev.color] || COLOR_BADGE.emerald} border-0 text-[10px] uppercase tracking-wide`}>
                          {t(`calendar.categories.${ev.category}`) || ev.category}
                        </Badge>
                        <DemoWriteBadge state={ev._demo_state} />
                      </div>
                      <div className="mt-1 text-xs text-slate-600 flex flex-wrap gap-3">
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" />
                          {fmtDate(ev.starts_at)} → {fmtDate(ev.ends_at)}
                        </span>
                        {ev.location && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {ev.location}
                          </span>
                        )}
                        {Array.isArray(ev.reminders) && ev.reminders.length > 0 && (
                          <span className="inline-flex items-center gap-1">
                            <Bell className="w-3 h-3" />
                            {ev.reminders.length}
                          </span>
                        )}
                      </div>
                      {ev.description && (
                        <p className="mt-1 text-sm text-slate-700 line-clamp-2">{ev.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(ev)} data-testid={`my-calendar-edit-${ev.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteEvent(ev)} data-testid={`my-calendar-delete-${ev.id}`}>
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Create / edit modal — centered (A20-P1.1-fix) */}
        <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DialogContent
            className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="my-calendar-drawer"
          >
            <DialogHeader>
              <DialogTitle>{t("calendar.form.heading")}</DialogTitle>
              <DialogDescription className="sr-only">
                {t("calendar.form.heading")}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-3">
              <label className="text-sm block">
                <span className="block text-slate-600 mb-1">{t("calendar.form.titleLabel")} *</span>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  maxLength={200}
                  data-testid="my-calendar-form-title"
                />
              </label>
              <label className="text-sm block">
                <span className="block text-slate-600 mb-1">{t("calendar.form.descriptionLabel")}</span>
                <Textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  maxLength={2000}
                  data-testid="my-calendar-form-description"
                />
              </label>
              <label className="text-sm block">
                <span className="block text-slate-600 mb-1">{t("calendar.form.locationLabel")}</span>
                <Input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  maxLength={200}
                  data-testid="my-calendar-form-location"
                />
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-sm">
                  <span className="block text-slate-600 mb-1">{t("calendar.form.startsAtLabel")} *</span>
                  <Input
                    type="datetime-local"
                    value={form.starts_at}
                    min={!editingId ? new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : undefined}
                    onChange={(e) => {
                      const next = e.target.value;
                      // A20-P1 — auto-shift end if the new start would
                      // make the end invalid. Default shift: +1h after the
                      // new start, computed in local-time space so the
                      // datetime-local input keeps the right format.
                      setForm((f) => {
                        const draft = { ...f, starts_at: next };
                        if (next && f.ends_at && f.ends_at <= next) {
                          try {
                            const startMs = new Date(next).getTime();
                            const oneHourAfter = new Date(startMs + 60 * 60 * 1000);
                            // Reuse isoToLocal to keep the same yyyy-MM-ddTHH:mm shape.
                            const tzOffset = oneHourAfter.getTimezoneOffset() * 60000;
                            draft.ends_at = new Date(oneHourAfter.getTime() - tzOffset)
                              .toISOString()
                              .slice(0, 16);
                          } catch { /* leave as-is if parsing fails */ }
                        }
                        return draft;
                      });
                    }}
                    data-testid="my-calendar-form-starts"
                    aria-invalid={!editingId && form.starts_at && new Date(form.starts_at).getTime() < Date.now() - 60_000}
                  />
                  {!editingId && form.starts_at && new Date(form.starts_at).getTime() < Date.now() - 60_000 && (
                    <span
                      className="block text-xs text-rose-700 mt-1"
                      data-testid="my-calendar-form-starts-error"
                    >
                      {t("calendar.toasts.startInPast")}
                    </span>
                  )}
                </label>
                <label className="text-sm">
                  <span className="block text-slate-600 mb-1">{t("calendar.form.endsAtLabel")} *</span>
                  <Input
                    type="datetime-local"
                    value={form.ends_at}
                    min={form.starts_at || undefined}
                    onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                    data-testid="my-calendar-form-ends"
                    aria-invalid={form.starts_at && form.ends_at && form.ends_at <= form.starts_at}
                  />
                  {form.starts_at && form.ends_at && form.ends_at <= form.starts_at && (
                    <span
                      className="block text-xs text-rose-700 mt-1"
                      data-testid="my-calendar-form-ends-error"
                    >
                      {t("calendar.toasts.endBeforeStart")}
                    </span>
                  )}
                </label>
              </div>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.all_day}
                  onChange={(e) => setForm({ ...form, all_day: e.target.checked })}
                  data-testid="my-calendar-form-allday"
                />
                {t("calendar.form.allDayLabel")}
              </label>
              {/* A20-P1.1 — Multi-category checkbox grid. Replaces the
                  legacy single <select>. The legacy `category` field is
                  auto-synced to the first selected entry (or kept as-is
                  when nothing is selected) so older read paths keep
                  working without a migration. */}
              <div data-testid="my-calendar-form-categories">
                <span className="block text-sm text-slate-600 mb-2">{t("calendar.form.categoriesLabel")}</span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                  {ALL_CATEGORIES.map((c) => {
                    const checked = (form.categories || []).includes(c);
                    return (
                      <label
                        key={c}
                        className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded border cursor-pointer ${
                          checked
                            ? "bg-emerald-50 border-emerald-400 text-emerald-900"
                            : "bg-white border-slate-200 text-slate-700 hover:border-emerald-300"
                        }`}
                        data-testid={`my-calendar-form-cat-${c}`}
                      >
                        <input
                          type="checkbox"
                          className="accent-emerald-600"
                          checked={checked}
                          onChange={(e) => {
                            setForm((f) => {
                              const next = new Set(f.categories || []);
                              if (e.target.checked) next.add(c); else next.delete(c);
                              const arr = Array.from(next);
                              // Keep legacy singular `category` synced so old
                              // read paths (list view badge / filter) still
                              // render correctly when the chosen value is
                              // part of the legacy 7-element vocabulary.
                              const legacy = arr.length > 0
                                ? (CATEGORIES.includes(arr[0]) ? arr[0] : f.category)
                                : f.category;
                              // Clear other_category_text when "other" is
                              // unchecked so we never send orphan text.
                              const otherText = next.has("other") ? f.other_category_text : "";
                              return { ...f, categories: arr, category: legacy, other_category_text: otherText };
                            });
                          }}
                        />
                        <span className="truncate">{t(`calendar.categories.${c}`)}</span>
                      </label>
                    );
                  })}
                </div>
                {(form.categories || []).includes("other") && (
                  <div className="mt-2" data-testid="my-calendar-form-other-wrap">
                    <label className="text-sm block">
                      <span className="block text-slate-600 mb-1">
                        {t("calendar.form.otherLabel")} *
                      </span>
                      <Input
                        value={form.other_category_text}
                        onChange={(e) => setForm({ ...form, other_category_text: e.target.value })}
                        maxLength={150}
                        placeholder={t("calendar.form.otherPlaceholder")}
                        data-testid="my-calendar-form-other"
                        aria-invalid={!form.other_category_text.trim()}
                      />
                      <div className="flex items-center justify-between mt-1 text-[11px]">
                        <span className={!form.other_category_text.trim() ? "text-rose-700" : "text-slate-500"}>
                          {!form.other_category_text.trim() ? t("calendar.form.otherRequired") : ""}
                        </span>
                        <span className="text-slate-500 tabular-nums">
                          {(form.other_category_text || "").length}/150
                        </span>
                      </div>
                    </label>
                  </div>
                )}
              </div>

              {/* A20-P1.1 — 13-color swatch picker. Writes to color_key.
                  The legacy `color` field is auto-mirrored when the chosen
                  color exists in the 6-value legacy enum so older list
                  view rail/badge keep their colour without a migration. */}
              <div data-testid="my-calendar-form-color-swatches">
                <span className="block text-sm text-slate-600 mb-2">{t("calendar.form.colorLabel")}</span>
                <div className="flex flex-wrap gap-1.5 items-center">
                  {COLOR_KEYS.map((c) => {
                    const active = form.color_key === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setForm((f) => ({
                          ...f,
                          color_key: c,
                          color: COLORS.includes(c) ? c : f.color,
                        }))}
                        title={c}
                        aria-label={c}
                        aria-pressed={active}
                        className={`w-7 h-7 rounded-full ${COLOR_CLASS[c]?.dot || "bg-slate-400"} border-2 transition-all ${
                          active ? "border-slate-900 ring-2 ring-offset-1 ring-emerald-400 scale-110" : "border-white shadow-sm hover:scale-110"
                        }`}
                        data-testid={`my-calendar-form-color-${c}`}
                      />
                    );
                  })}
                  {form.color_key && (
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, color_key: "" }))}
                      className="text-[11px] text-slate-500 underline ms-2"
                      data-testid="my-calendar-form-color-reset"
                      aria-label="reset color"
                    >
                      ↺
                    </button>
                  )}
                </div>
              </div>
              <div>
                <span className="block text-sm text-slate-600 mb-2">{t("calendar.form.remindersLabel")}</span>
                <div className="flex flex-wrap gap-1.5" data-testid="my-calendar-form-reminders">
                  {REMINDER_MINUTES.map((m) => {
                    const on = form.reminders.includes(m);
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => toggleReminder(m)}
                        className={`text-xs px-2 py-1 rounded-full border ${
                          on
                            ? "bg-emerald-600 border-emerald-600 text-white"
                            : "bg-white border-slate-300 text-slate-700 hover:border-emerald-400"
                        }`}
                        data-testid={`my-calendar-form-reminder-${m}`}
                      >
                        {t(`calendar.reminderOptions.${m}`)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="text-[11px] text-slate-500">{t("calendar.form.hint")}</p>
              {editingId && (
                <div className="pt-3 mt-3 border-t border-slate-200" data-testid="my-calendar-budget-section">
                  <BudgetPanel eventId={editingId} />
                </div>
              )}
              {editingId && (
                <LinkedWishlistItems eventId={editingId} t={t} />
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDrawerOpen(false)} data-testid="my-calendar-form-cancel">
                  {t("calendar.cancel")}
                </Button>
                <Button
                  onClick={submitForm}
                  disabled={saving || !form.title.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="my-calendar-form-save"
                >
                  {t("calendar.save")}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
    </PanelLayout>
  );
};

export default MyCalendar;
